import React, { useCallback } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { DOCUMENT_ACTIONS, MODAL_ACTIONS, MODAL_SEVERITY, MODAL_SIZE, TASK_ACTIONS } from "utils/constants";
import { Modal } from "components/modals/Modal";
import { ModalRouterProps } from "types/modals";

import Postpone from "components/modals/Postpone.modal";
import Reject from "components/modals/Reject.modal";
import Forward from "components/modals/Forward.modal";
import DeleteAttachment from "components/modals/DeleteAttachment.modal";
import ErrorModal from "components/modals/Error.modal";
import AddAttachment from "components/modals/AddAttachment.modal";
import RequestReview from "components/modals/RequestReview.modal";
import Reassign from "components/modals/Reassign.modal";
import CancelDocuments from "components/modals/CancelDocuments.modal";
import ReviewTask from "components/modals/ReviewTask.modal";
import HandledTaskModal from "components/modals/HandledTask.modal";
import RejectDirectly from "components/modals/RejectDirectly.modal";
import ApproveDirectly from "components/modals/ApproveDirectly.modal";

const TYPE_TO_COMPONENT: Record<string, React.ComponentType<any>> = {
    [TASK_ACTIONS.REJECT]: Reject,
    [TASK_ACTIONS.FORWARD]: Forward,
    [TASK_ACTIONS.DELETE_ATTACHMENT]: DeleteAttachment,
    [TASK_ACTIONS.ADD_ATTACHMENT]: AddAttachment,
    [TASK_ACTIONS.POSTPONE]: Postpone,
    [TASK_ACTIONS.SEND_FOR_REVIEW]: RequestReview,
    [TASK_ACTIONS.REASSIGN]: Reassign,
    [TASK_ACTIONS.REVIEW]: ReviewTask,
    [DOCUMENT_ACTIONS.CANCEL_DIRECTLY]: CancelDocuments,
    [DOCUMENT_ACTIONS.APPROVE_DIRECTLY]: ApproveDirectly,
    [DOCUMENT_ACTIONS.REJECT_DIRECTLY]: RejectDirectly,
    [MODAL_ACTIONS.ERROR]: ErrorModal,
    [MODAL_ACTIONS.HANDLED_TASK]: HandledTaskModal,
};

interface ModalConfigEntry {
    labelKey?: string;
    titleKey?: string;
    descriptionKey?: string;
}

const MODAL_CONFIG: Record<string, ModalConfigEntry> = {
    [TASK_ACTIONS.REJECT]: {
        titleKey: "popUp.reject.title",
    },
    [TASK_ACTIONS.FORWARD]: {
        titleKey: "popUp.forward.title",
    },
    [TASK_ACTIONS.DELETE_ATTACHMENT]: {
        titleKey: "popUp.deleteAttachment.title",
        descriptionKey: "popUp.deleteAttachment.message",
    },
    [TASK_ACTIONS.ADD_ATTACHMENT]: {
        titleKey: "popUp.addAttachment.title",
    },
    [TASK_ACTIONS.POSTPONE]: {
        titleKey: "popUp.postpone.title",
        descriptionKey: "popUp.postpone.postponeExplanation",
    },
    [TASK_ACTIONS.SEND_FOR_REVIEW]: {
        titleKey: "popUp.sendforreview.title",
    },
    [TASK_ACTIONS.REASSIGN]: {
        titleKey: "popUp.reassign.title",
    },
    [TASK_ACTIONS.REVIEW]: {
        titleKey: "popUp.review.title",
    },
    [DOCUMENT_ACTIONS.CANCEL_DIRECTLY]: {
        titleKey: "popUp.cancelDocuments.title",
    },
    [DOCUMENT_ACTIONS.APPROVE_DIRECTLY]: {
        titleKey: "popUp.approveDirectly.titleSingle",
    },
    [DOCUMENT_ACTIONS.REJECT_DIRECTLY]: {
        titleKey: "popUp.rejectDirectly.titleSingle",
    },
    [MODAL_ACTIONS.ERROR]: {
        titleKey: "popUp.error.title",
    },
    [MODAL_ACTIONS.HANDLED_TASK]: {
        titleKey: "popUp.handledTaskWarning.title",
    },
};

export const ModalRouter = ({ type, more, toggleModal }: ModalRouterProps): React.ReactElement => {
    const { translate } = useTranslation();

    const config = type ? MODAL_CONFIG[type] ?? {} : {};
    const title = more?.title ?? (config.titleKey ? translate(config.titleKey) : undefined);
    const label = more?.label ?? (config.labelKey ? translate(config.labelKey) : undefined);
    const description = more?.description ?? (config.descriptionKey ? translate(config.descriptionKey) : undefined);
    const severity = more?.severity || null;
    const size = more?.size || MODAL_SIZE.MEDIUM;

    const closeModal = useCallback(() => {
        more?.closeCallback?.();
        toggleModal(null);
    }, [more, toggleModal]);

    const errorCallback = useCallback(
        (error: Record<string, any>) => {
            closeModal();
            toggleModal(MODAL_ACTIONS.ERROR, { ...error, severity: MODAL_SEVERITY.DANGER });
        },
        [closeModal, toggleModal],
    );

    const Component = type ? TYPE_TO_COMPONENT[type] : null;

    return (
        <Modal
            open={!!Component}
            onClose={closeModal}
            label={label}
            title={title}
            description={description}
            severity={severity}
            size={size}
        >
            {({ loopFocus }) =>
                Component && (
                    <Component
                        className="position-absolute z-index-50"
                        loopFocus={loopFocus}
                        closeModal={closeModal}
                        errorCallback={errorCallback}
                        more={more}
                    />
                )
            }
        </Modal>
    );
};

export default ModalRouter;
